import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { type YatPageItem } from '@app/client/common/structs/yatPage/yat-page-items';
import { processPageItem } from '@app/helpers/yatPage/content/processPageItem';
import { addLiquidIDItem, clearToggledItems, setYatPageLiquidID } from '@app/store/slices/yat-page';

import { type RootState } from '../reducers';
import { isContentItemNft } from '../slices/yat-page/types';
import {
    ACTION_ADD_UNSUPPORTED_NFT,
    ACTION_CLEAR_MANAGER_SELECTION,
    ACTION_DISPLAY_SNACKBAR,
    ACTION_PREVIEW,
    ACTION_SELECT_ALL_MANAGER_ITEMS,
    ACTION_SELECT_MANAGER_ITEMS,
    ACTION_SET_MANAGER_PANEL_CLOSED,
    ACTION_SET_MANAGER_PANEL_OPEN,
    ACTION_SET_MANAGER_SEARCH_VALUE,
    ACTION_SET_MANAGER_START_DRAGGING_NFT,
    ACTION_SET_MANAGER_STOP_DRAGGING_NFT,
    ACTION_TOGGLE_MANAGER_PANEL_MINIFIED,
    ACTION_UPDATE_MANAGER_NFT_FILTERS,
    type NFTItem,
    type NFTManagerFilter,
} from '../types/nft-manager';

import { addItem, addItemsBulk, removeItem, removeItemsBulk } from './yat-page-item-actions';

export const setNFTManagerPanelOpen = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_SET_MANAGER_PANEL_OPEN });
    };
};

export const setNFTManagerPanelClose = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(clearToggledItems());
        dispatch({ type: ACTION_SET_MANAGER_PANEL_CLOSED });
    };
};

export const toggleNFTManagerPanelCollapsed = (collapsed?: boolean) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const editingLiquidID = getState().yatPage.editStates.editingLiquidID;
        const draggingElements = getState().nftManager.draggingItems.length > 0;
        if (editingLiquidID || draggingElements) return;

        dispatch({ type: ACTION_TOGGLE_MANAGER_PANEL_MINIFIED, collapsed });
    };
};

export const selectAllManagerItems = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_SELECT_ALL_MANAGER_ITEMS });
    };
};

export const selectManagerItems = (items: string | string[], inPage: boolean) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_SELECT_MANAGER_ITEMS, items, inPage });
    };
};

export const clearManagerSelection = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_CLEAR_MANAGER_SELECTION });
    };
};

export const setManagerStartDragging = (mouseEvent: MouseEvent, item: string) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_SET_MANAGER_START_DRAGGING_NFT, mouseEvent, item });
    };
};

export const setManagerStopDragging = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_SET_MANAGER_STOP_DRAGGING_NFT });
    };
};

export const addUnsupportedNft = (unsupportedNft: string) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({ type: ACTION_ADD_UNSUPPORTED_NFT, unsupportedNft });
    };
};

export const setNFTManagerSearchValue = (managerSearchValue: string) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({
            type: ACTION_SET_MANAGER_SEARCH_VALUE,
            managerSearchValue,
        });
    };
};

export const updateNFTManagerFilters = ({ filter, clear = false }: { filter?: NFTManagerFilter; clear?: boolean }) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({
            type: ACTION_UPDATE_MANAGER_NFT_FILTERS,
            filter,
            clear,
        });
    };
};

export type QueryItemsType = (
    | { nft: boolean }
    | { metadata_complete?: boolean }
    | { collection_name?: string }
    | { nft_name?: string }
    | { floor_price_le: number }
    | { floor_price_ge: number }
    | { owner_address: string }
    | { owner_addresses: string[] }
    | { spam: boolean }
)[];

type QueryItemsProps = {
    searchValue?: string;
    operator: 'OR' | 'AND';
    currentFilters: NFTManagerFilter;
};
export const getQueryItems = ({ searchValue, operator, currentFilters }: QueryItemsProps) => {
    const queryItems: QueryItemsType = operator == 'OR' ? [] : [{ nft: true }, { metadata_complete: true }];

    // Adds current available filters
    if (searchValue) {
        queryItems.push({ collection_name: searchValue });
        queryItems.push({ nft_name: searchValue });
    } else {
        currentFilters.floorPriceLe && queryItems.push({ floor_price_le: currentFilters.floorPriceLe });
        currentFilters.floorPriceGe && queryItems.push({ floor_price_ge: currentFilters.floorPriceGe });
        currentFilters.ownerAddress?.length && queryItems.push({ owner_addresses: currentFilters.ownerAddress });
        queryItems.push({ spam: !!currentFilters.spam });
    }

    return queryItems;
};

export const serializeResponseItems = (items: YatPageItem[]): NFTItem[] =>
    items
        .map(processPageItem)
        .filter(isContentItemNft)
        .map((item) => ({
            ...item,
            hasPersisted: false,
        }));

export const quickAddItem = (item: NFTItem, liquidIDFace?: number) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const {
            editStates: { editingLiquidID },
            liquidIDFaceSelection,
        } = getState().yatPage;

        if (editingLiquidID) {
            dispatch(addLiquidIDItem({ item, face: liquidIDFace || liquidIDFaceSelection }));
        } else {
            dispatch(addItem(item));
        }
    };
};

export const addSelectedItems = (items: NFTItem[]) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(addItemsBulk(items));
        dispatch({ type: ACTION_PREVIEW });
    };
};

export const handleRemoveItemsToYatPage = (itemsToRemove: string[]) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(removeItemsBulk(itemsToRemove));
    };
};

export const quickRemoveItem = (itemToUpdate: NFTItem) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const editingLiquidID = getState().yatPage.editStates.editingLiquidID;
        const liquidIDItems = getState().yatPage.liquidIDItems;

        if (editingLiquidID) {
            const currentItems = [...liquidIDItems];
            const foundItemIndex = liquidIDItems.findIndex((item) => item !== undefined && item.id === itemToUpdate.id);
            currentItems[foundItemIndex] = undefined;

            dispatch(setYatPageLiquidID({ items: currentItems }));
        } else {
            dispatch(removeItem(itemToUpdate.id));
        }
    };
};

export const toggleDisplaySnackbar = (direction, timeout) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch({
            type: ACTION_DISPLAY_SNACKBAR,
            direction,
        });

        if (timeout) {
            setTimeout(() => {
                dispatch({
                    type: ACTION_DISPLAY_SNACKBAR,
                    direction: !direction,
                });
            }, 2500);
        }
    };
};
